import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Eye, ListTree, Plus, RefreshCw, Rows3, Save, Settings2, Sigma, Table2, Type as TypeIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import { dataSourceApi, datasetApi } from "../../api/dataSources";
import { DatasetRowsGrid } from "../../components/DatasetRowsGrid";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  SearchInput,
  Segmented,
  Select,
  SkeletonRows,
  Switch,
  Tabs,
  formatRelative,
  useToast,
} from "../../ds";
import type { Dataset, DatasetField, DatasetMeasure } from "../../types";
import { describeMeasureContext } from "../../studio/measureContext";

type Tab = "fields" | "measures" | "preview" | "settings";
const AGGS: Array<{ value: DatasetMeasure["aggregation"]; label: string }> = [
  { value: "sum", label: "Sum" },
  { value: "average", label: "Average" },
  { value: "count", label: "Count rows" },
  { value: "count_distinct", label: "Count distinct" },
  { value: "minimum", label: "Minimum" },
  { value: "maximum", label: "Maximum" },
];
const TYPE_OPTIONS = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & time" },
  { value: "boolean", label: "Yes / No" },
];
// Types older tables were saved with before v2.2 normalised them.
const TYPE_LABEL_FALLBACK: Record<string, string> = { str: "Text", text: "Text", int: "Number", float: "Number", dateTime: "Date & time" };
const FORMATS = [
  { value: "", label: "Automatic" },
  { value: "number", label: "Number" },
  { value: "usd", label: "US dollars" },
  { value: "egp", label: "Egyptian pounds" },
  { value: "percent_raw", label: "Percent" },
  { value: "integer", label: "Whole number" },
];

export function DatasetsV2Page() {
  const [params, setParams] = useSearchParams();
  const datasets = useQuery({ queryKey: ["admin", "datasets"], queryFn: datasetApi.list });
  const sources = useQuery({ queryKey: ["admin", "data-sources"], queryFn: dataSourceApi.list });
  const runs = useQuery({ queryKey: ["admin", "refresh-runs"], queryFn: datasetApi.listRecentRefreshRuns });
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(!!params.get("source"));
  const selectedId = params.get("id");
  const srcName = useMemo(() => new Map((sources.data ?? []).map((s) => [s.id, s.name])), [sources.data]);
  const list = (datasets.data ?? []).filter((d) => !q || `${d.name} ${d.description ?? ""} ${srcName.get(d.data_source_id) ?? ""}`.toLowerCase().includes(q.toLowerCase()));
  const selected = (datasets.data ?? []).find((d) => d.id === selectedId) ?? null;
  const select = (id: string) => {
    params.set("id", id);
    setParams(params, { replace: true });
  };

  return (
    <div className="ds-page ds-page--wide" style={{ maxWidth: 1600 }}>
      <PageHeader
        eyebrow="Data"
        title="Datasets"
        subtitle="A dataset is one table from a source — for example one Excel sheet — with friendly field names and reusable measures. Every visual is built from a dataset."
        actions={<Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>New dataset</Button>}
      />
      <div className="ds-grid" style={{ gridTemplateColumns: "360px minmax(0, 1fr)", alignItems: "start" }}>
        <Card flush>
          <div style={{ padding: 12 }}>
            <SearchInput value={q} onChange={setQ} placeholder={`Search ${datasets.data?.length ?? ""} datasets`} width="100%" />
          </div>
          {datasets.isLoading ? <SkeletonRows rows={8} /> : list.length === 0 ? <EmptyState icon={Database} title="No datasets" description="Create one from a connected source." /> : (
            <div className="ds-list" style={{ maxHeight: "calc(100vh - 260px)", overflow: "auto" }}>
              {list.map((d) => {
                const lastRun = (runs.data ?? []).find((r) => r.dataset_id === d.id);
                return (
                  <div key={d.id} className="ds-list__row is-clickable" onClick={() => select(d.id)} style={d.id === selectedId ? { background: "var(--accent-soft)" } : undefined}>
                    <Table2 size={16} className="ds-faint" />
                    <div className="ds-list__main">
                      <div className="ds-list__title ds-truncate">{d.name}</div>
                      <div className="ds-list__meta ds-truncate">{srcName.get(d.data_source_id) ?? "—"}</div>
                    </div>
                    {lastRun?.status === "failed" ? <Badge tone="danger">Failed</Badge> : !d.is_active ? <Badge>Off</Badge> : null}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        {selected ? <DatasetDetail key={selected.id} ds={selected} sourceName={srcName.get(selected.data_source_id) ?? "—"} /> : (
          <Card><EmptyState icon={ListTree} title="Select a dataset" description="See its fields, add measures (like Total cost or Number of claims) and look through its real rows." /></Card>
        )}
      </div>
      <NewDataset open={creating} onClose={() => { setCreating(false); if (params.get("source")) { params.delete("source"); setParams(params, { replace: true }); } }} defaultSource={params.get("source")} onCreated={select} />
    </div>
  );
}

function DatasetDetail({ ds, sourceName }: { ds: Dataset; sourceName: string }) {
  const [tab, setTab] = useState<Tab>("fields");
  const qc = useQueryClient();
  const toast = useToast();
  const fields = useQuery({ queryKey: ["admin", "fields", ds.id], queryFn: () => datasetApi.listFields(ds.id) });
  const measures = useQuery({ queryKey: ["admin", "measures", ds.id], queryFn: () => datasetApi.listMeasures(ds.id) });
  const runs = useQuery({ queryKey: ["admin", "refresh-runs"], queryFn: datasetApi.listRecentRefreshRuns });
  const lastRun = (runs.data ?? []).find((r) => r.dataset_id === ds.id);
  const refresh = useMutation({
    mutationFn: () => datasetApi.refresh(ds.id),
    onSuccess: (r) => { toast.success("Refreshed", r.error ? r.error : `Fresh data will be used for the next view.`); qc.invalidateQueries({ queryKey: ["admin", "refresh-runs"] }); },
    onError: (e) => toast.error("Refresh failed", e instanceof Error ? e.message : undefined),
  });
  return (
    <Card
      title={ds.name}
      subtitle={`${sourceName}${(ds as Dataset & { source_sheet?: string }).source_sheet ? ` · sheet “${(ds as Dataset & { source_sheet?: string }).source_sheet}”` : ""}`}
      actions={<Button icon={RefreshCw} loading={refresh.isPending} onClick={() => refresh.mutate()}>Refresh now</Button>}
    >
      {ds.description ? <div className="ds-muted ds-small" style={{ marginTop: -6, marginBottom: 12 }}>{ds.description}</div> : null}
      <div className="ds-row" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Badge tone="info">{fields.data?.length ?? "…"} fields</Badge>
        <Badge tone="success">{measures.data?.length ?? "…"} measures</Badge>
        <Badge>{lastRun ? `last refresh ${formatRelative(lastRun.started_at)} · ${lastRun.status}` : "no refresh recorded"}</Badge>
      </div>
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[{ value: "fields", label: "Fields", icon: TypeIcon }, { value: "measures", label: "Measures", icon: Sigma }, { value: "preview", label: "Data", icon: Table2 }, { value: "settings", label: "Settings", icon: Settings2 }]} />
      <div style={{ paddingTop: 14 }}>
        {tab === "fields" ? <FieldsTab ds={ds} fields={fields.data} loading={fields.isLoading} /> : null}
        {tab === "measures" ? <MeasuresTab ds={ds} fields={fields.data ?? []} measures={measures.data} loading={measures.isLoading} /> : null}
        {tab === "preview" ? <PreviewTab ds={ds} fields={fields.data ?? []} measures={measures.data ?? []} /> : null}
        {tab === "settings" ? <SettingsTab ds={ds} /> : null}
      </div>
    </Card>
  );
}

export function FieldsTab({ ds, fields, loading }: { ds: Dataset; fields?: DatasetField[]; loading: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<DatasetField[]>([]);
  useEffect(() => setRows(fields ?? []), [fields]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(fields ?? []);
  const save = useMutation({
    mutationFn: () => datasetApi.replaceFields(ds.id, rows.map(({ id: _id, ...rest }) => rest)),
    onSuccess: () => { toast.success("Fields saved"); qc.invalidateQueries({ queryKey: ["admin", "fields", ds.id] }); },
    onError: (e) => toast.error("Couldn't save fields", e instanceof Error ? e.message : undefined),
  });
  const set = (i: number, patch: Partial<DatasetField>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  if (loading) return <SkeletonRows />;
  return (
    <div className="ds-stack">
      <DataTable<DatasetField & { _i: number }>
        rows={rows.map((r, i) => ({ ...r, _i: i }))}
        rowKey={(r) => r.id ?? r.source_name}
        maxHeight={460}
        empty={<EmptyState icon={TypeIcon} title="No columns yet" description="Use “Detect columns” above — the platform reads them from the source." />}
        columns={[
          { key: "src", header: "Column in source", cell: (r) => <span className="ds-mono ds-muted">{r.source_name}</span> },
          { key: "name", header: "Name people see", cell: (r) => <Input inputSize="sm" value={r.display_name} onChange={(e) => set(r._i, { display_name: e.target.value })} /> },
          { key: "type", header: "Type", cell: (r) => <Select inputSize="sm" value={r.data_type} onChange={(e) => set(r._i, { data_type: e.target.value })}>{[...TYPE_OPTIONS, ...(TYPE_OPTIONS.some((t) => t.value === r.data_type) ? [] : [{ value: r.data_type, label: `${TYPE_LABEL_FALLBACK[r.data_type] ?? r.data_type} (as detected)` }])].map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</Select> },
          { key: "role", header: "Used as", cell: (r) => <Select inputSize="sm" value={r.field_role} onChange={(e) => set(r._i, { field_role: e.target.value as DatasetField["field_role"] })}><option value="dimension">Category / label</option><option value="measure">Number</option><option value="date">Date</option><option value="identifier">ID</option><option value="geography">Place</option></Select> },
          { key: "vis", header: "Visible", cell: (r) => <Switch checked={r.is_visible} onChange={(v) => set(r._i, { is_visible: v })} /> },
        ]}
      />
      <div className="ds-row" style={{ justifyContent: "flex-end" }}>
        <Button disabled={!dirty} onClick={() => setRows(fields ?? [])}>Discard</Button>
        <Button variant="primary" icon={Save} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save fields</Button>
      </div>
    </div>
  );
}

export function MeasuresTab({ ds, fields, measures, loading }: { ds: Dataset; fields: DatasetField[]; measures?: DatasetMeasure[]; loading: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [field, setField] = useState("");
  const [agg, setAgg] = useState<DatasetMeasure["aggregation"]>("sum");
  const [fmt, setFmt] = useState("");
  const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const create = useMutation({
    mutationFn: () => datasetApi.createMeasure(ds.id, { name: slug, display_name: label.trim(), source_field: field, aggregation: agg, ...(fmt ? { format_string: fmt } : {}) }),
    onSuccess: () => { toast.success("Measure added", `“${label}” is now available in the designer.`); setOpen(false); setLabel(""); setField(""); qc.invalidateQueries({ queryKey: ["admin", "measures", ds.id] }); },
    onError: (e) => toast.error("Couldn't add the measure", e instanceof Error ? e.message : undefined),
  });
  if (loading) return <SkeletonRows />;
  return (
    <div className="ds-stack">
      <div className="ds-row">
        <div className="ds-grow ds-small ds-muted">Measures are the numbers visuals show, e.g. “Total cost” = Sum of Cost.</div>
        <Button icon={Plus} onClick={() => setOpen(true)}>Add measure</Button>
      </div>
      <DataTable<DatasetMeasure>
        rows={measures}
        rowKey={(m) => m.id}
        empty={<EmptyState icon={Sigma} title="No measures yet" description="Add one to start building KPIs and charts." action={<Button variant="primary" icon={Plus} onClick={() => setOpen(true)}>Add measure</Button>} />}
        columns={[
          { key: "n", header: "Measure", cell: (m) => <div><div style={{ fontWeight: 600 }}>{m.display_name || m.name}</div><div className="ds-mono ds-faint">{m.name}</div></div> },
          { key: "f", header: "Calculation", cell: (m) => {
            const context = describeMeasureContext(m);
            return (
              <div>
                {m.expression ? <span className="ds-mono">{m.expression}</span> : m.source_measure ? null : <span>{AGGS.find((a) => a.value === m.aggregation)?.label ?? m.aggregation} of <strong>{fields.find((f) => f.source_name === m.source_field)?.display_name ?? m.source_field}</strong></span>}
                {context ? <div className="ds-small ds-faint">{context}</div> : null}
              </div>
            );
          } },
          { key: "fmt", header: "Format", cell: (m) => <span className="ds-muted">{m.format_string || "Automatic"}</span> },
        ]}
      />
      <Modal open={open} onClose={() => setOpen(false)} title="Add a measure" footer={<><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="primary" loading={create.isPending} disabled={!slug || !field} onClick={() => create.mutate()}>Add measure</Button></>}>
        <div className="ds-stack">
          <Field label="Name" hint={slug ? `Reference name: ${slug}` : "e.g. Total cost"}><Input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus /></Field>
          <div className="ds-grid ds-grid--2">
            <Field label="Calculation"><Select value={agg} onChange={(e) => setAgg(e.target.value as DatasetMeasure["aggregation"])}>{AGGS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</Select></Field>
            <Field label="of field">
              <Select value={field} onChange={(e) => setField(e.target.value)}>
                <option value="">Choose…</option>
                {fields.filter((f) => agg === "count" || agg === "count_distinct" || f.data_type === "number" || f.field_role === "measure").map((f) => <option key={f.id} value={f.source_name}>{f.display_name}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="Number format"><Select value={fmt} onChange={(e) => setFmt(e.target.value)}>{FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}</Select></Field>
        </div>
      </Modal>
    </div>
  );
}

export function PreviewTab({ ds, fields, measures }: { ds: Dataset; fields: DatasetField[]; measures: DatasetMeasure[] }) {
  const [view, setView] = useState<"rows" | "summary">("rows");
  const toggle = (
    <Segmented<"rows" | "summary">
      size="sm"
      value={view}
      onChange={setView}
      options={[{ value: "rows", label: "Rows", icon: Rows3, title: "Every row, exactly as the source has it" }, { value: "summary", label: "Summary", icon: Sigma, title: "Rows grouped by the first fields, with a measure" }]}
    />
  );
  if (view === "rows") return <DatasetRowsGrid area="admin" datasetId={ds.id} toolbarExtra={toggle} />;
  return <SummaryPreview ds={ds} fields={fields} measures={measures} toggle={toggle} />;
}

/** The grouped preview (first visible fields + an optional measure), now
 * reading the executor's real {status, data: {rows}} shape and showing the
 * source's own error instead of "Nothing to preview". */
function SummaryPreview({ ds, fields, measures, toggle }: { ds: Dataset; fields: DatasetField[]; measures: DatasetMeasure[]; toggle: ReactNode }) {
  const dims = fields.filter((f) => f.is_visible && f.field_role !== "measure").slice(0, 6).map((f) => f.source_name);
  const [m, setM] = useState<string>("");
  const preview = useQuery({
    queryKey: ["admin", "preview", ds.id, dims.join("|"), m],
    queryFn: () => datasetApi.preview(ds.id, { dimensions: dims, measures: m ? [m] : [], limit: 50 }),
    enabled: dims.length > 0,
    retry: false,
  });
  const data = preview.data as { status?: string; error?: string; data?: { rows?: Array<Record<string, unknown>> } } | undefined;
  const cols = [...dims, ...(m ? [m] : [])];
  const rows = (data?.data?.rows ?? []).map((r, i) => ({ r, i }));
  const label = (c: string) => fields.find((f) => f.source_name === c)?.display_name ?? measures.find((x) => x.name === c)?.display_name ?? c;
  return (
    <div className="ds-stack">
      <div className="ds-row" style={{ flexWrap: "wrap" }}>
        <div className="ds-grow ds-small ds-muted">Up to 50 groups of the first {dims.length} visible fields{m ? ", with the selected measure" : ""}.</div>
        <Select inputSize="sm" value={m} onChange={(e) => setM(e.target.value)} style={{ width: 220 }} aria-label="Measure">
          <option value="">No measure</option>
          {measures.map((x) => <option key={x.id} value={x.name}>{x.display_name || x.name}</option>)}
        </Select>
        {toggle}
      </div>
      {dims.length === 0 ? <EmptyState icon={Eye} title="No fields to group by" description="Make some fields visible on the Fields tab first." /> : preview.isLoading ? <SkeletonRows rows={8} /> : preview.isError ? <Banner tone="danger" title="Preview failed">{preview.error instanceof Error ? preview.error.message : null}</Banner> : data?.status === "unavailable" ? <Banner tone="danger" title="Couldn't read this data right now">{data.error || "The data source didn't answer."}</Banner> : rows.length === 0 ? <EmptyState icon={Eye} title="No rows" description="The source returned an empty table." /> : (
        <DataTable<{ r: Record<string, unknown>; i: number }>
          rows={rows}
          rowKey={(x) => String(x.i)}
          maxHeight={460}
          columns={cols.map((c) => ({ key: c, header: label(c), sortValue: (x) => x.r[c] as string | number, align: typeof rows[0]?.r[c] === "number" ? "right" : undefined, cell: (x) => (typeof x.r[c] === "number" ? (x.r[c] as number).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(x.r[c] ?? "—")) }))}
        />
      )}
    </div>
  );
}

function SettingsTab({ ds }: { ds: Dataset }) {
  const d = ds as Dataset & { source_sheet?: string | null; header_row?: number | null };
  return (
    <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "200px 1fr", gap: "10px 16px" }}>
      <dt className="ds-muted">Source object</dt><dd style={{ margin: 0 }} className="ds-mono">{ds.source_object}</dd>
      <dt className="ds-muted">Sheet</dt><dd style={{ margin: 0 }}>{d.source_sheet ?? "—"}</dd>
      <dt className="ds-muted">Header row</dt><dd style={{ margin: 0 }}>{(d.header_row ?? 0) + 1}</dd>
      <dt className="ds-muted">Cache</dt><dd style={{ margin: 0 }}>Keeps results for {Math.round(ds.cache_ttl_seconds / 60)} min, then refreshes in the background</dd>
      <dt className="ds-muted">Status</dt><dd style={{ margin: 0 }}>{ds.is_active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</dd>
      <dt className="ds-muted">Created</dt><dd style={{ margin: 0 }}>{formatRelative(ds.created_at)}</dd>
    </dl>
  );
}

function NewDataset({ open, onClose, defaultSource, onCreated }: { open: boolean; onClose: () => void; defaultSource: string | null; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const sources = useQuery({ queryKey: ["admin", "data-sources"], queryFn: dataSourceApi.list, enabled: open });
  const [source, setSource] = useState(defaultSource ?? "");
  const [name, setName] = useState("");
  const [object, setObject] = useState("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setSource(defaultSource ?? ""); setName(""); setObject(""); setErr(null); } }, [open, defaultSource]);
  const create = useMutation({
    mutationFn: () => datasetApi.create({ name: name.trim(), data_source_id: source, source_object: object.trim() || name.trim(), cache_ttl_seconds: 300 }),
    onSuccess: (d) => { toast.success("Dataset created", "Its fields are discovered on the first refresh."); qc.invalidateQueries({ queryKey: ["admin", "datasets"] }); onClose(); onCreated(d.id); },
    onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't create the dataset"),
  });
  const src = sources.data?.find((s) => s.id === source);
  return (
    <Modal open={open} onClose={onClose} title="New dataset" description="Pick where the data lives and which sheet or table to read." footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={create.isPending} disabled={!source || !name.trim()} onClick={() => create.mutate()}>Create dataset</Button></>}>
      <div className="ds-stack">
        <Field label="Source"><Select value={source} onChange={(e) => setSource(e.target.value)}><option value="">Choose a data source…</option>{(sources.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
        <Field label="Dataset name" hint="How it appears in the designer, e.g. “Claims 2026”."><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label={src?.type === "excel" ? "Sheet name" : "Table / list name"} hint={src?.type === "excel" ? "Exactly as the tab is named in Excel." : "Leave empty to use the dataset name."} error={err ?? undefined}><Input value={object} onChange={(e) => setObject(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
